/**
 * Session message loader — extracted from useChat.ts (issue #3280).
 *
 * Holds the body of the "load messages from server when sessionId changes
 * (conversation switch)" effect. The orchestrator keeps the `useEffect` shell
 * AND its byte-identical `[sessionId, token]` dependency array; only the effect
 * body lives here as a pure delegating function. Behavior is preserved exactly —
 * this is a relocation, not a logic change.
 *
 * Web-internal module: must NOT be promoted to a shared `@xavior/*` package
 * (apps/web/AGENTS.md forbids web → domain/persistence/etc.). It depends on
 * web-only api endpoints and React state setters.
 */
import type React from 'react'
import { getConversationChatMessages } from '@/lib/api/endpoints/chat'
import { serverToClientMessage } from '../utils/message-mapping'
import type { ChatMessage, QuickAction, StreamActivityStep } from './useChat.types'

export interface SessionMessageLoaderDeps {
  sessionId: string | undefined
  token: string | null
  previousSessionIdRef: React.MutableRefObject<string | undefined>
  greetingAbortControllerRef: React.MutableRefObject<AbortController | null>
  greetingCancelledRef: React.MutableRefObject<boolean>
  greetingInProgressRef: React.MutableRefObject<boolean>
  bypassedNewConversationIdRef: React.MutableRefObject<string | null>
  lastCreatedConversationIdRef: React.MutableRefObject<string | null>
  clearLastCreatedConversationIdRef: React.MutableRefObject<() => void>
  currentSessionIdRef: React.MutableRefObject<string | undefined>
  streamActivityRef: React.MutableRefObject<StreamActivityStep[]>
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  setThinking: React.Dispatch<React.SetStateAction<string | null>>
  setStreamActivity: React.Dispatch<React.SetStateAction<StreamActivityStep[]>>
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<Error | null>>
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>
  setPendingQuickActions: React.Dispatch<React.SetStateAction<QuickAction[]>>
}

/**
 * Body of the session-switch message-load effect. Extracted verbatim from
 * useChat.ts; the caller wraps this in `useEffect(..., [sessionId, token])`.
 */
export function loadSessionMessages(deps: SessionMessageLoaderDeps): void {
  const {
    sessionId,
    token,
    previousSessionIdRef,
    greetingAbortControllerRef,
    greetingCancelledRef,
    greetingInProgressRef,
    bypassedNewConversationIdRef,
    lastCreatedConversationIdRef,
    clearLastCreatedConversationIdRef,
    currentSessionIdRef,
    streamActivityRef,
    setMessages,
    setThinking,
    setStreamActivity,
    setIsStreaming,
    setIsLoading,
    setError,
    setStreamingMessage,
    setPendingQuickActions,
  } = deps

  const requestedSessionId = sessionId
  const didSessionChange = previousSessionIdRef.current !== requestedSessionId
  previousSessionIdRef.current = requestedSessionId

  if (didSessionChange) {
    // Abort any in-flight greeting from the previous conversation so it can't
    // call setStreamingMessage / setIsStreaming after we've switched away.
    if (greetingAbortControllerRef.current) {
      greetingAbortControllerRef.current.abort()
      greetingAbortControllerRef.current = null
    }
    greetingCancelledRef.current = true
    greetingInProgressRef.current = false
    // Don't clear greetingLastRequestedRef — keep the dedup window active
    // so a rapid switch-back can't re-trigger a greeting
    // Reset the new-conversation bypass so a later switch back to a
    // previously-bypassed conversation loads persisted history normally.
    bypassedNewConversationIdRef.current = null
  }

  if (!sessionId || sessionId.startsWith('conversation-')) {
    // No session or temp optimistic ID — wait for real server ID before loading
    setMessages([])
    setThinking(null)
    streamActivityRef.current = []
    setStreamActivity([])
    setIsStreaming(false)
    setIsLoading(false)
    setError(null)
    setStreamingMessage(null)
    // Clear any quick-action chips left over from the previous conversation
    // so they don't visually persist into the new (or empty) one (#1261).
    setPendingQuickActions([])
    return
  }

  // Bypass the server GET for a freshly-created conversation. Otherwise the
  // GET returns [] before the server has persisted the greeting, and
  // `setMessages(deduped)` wipes the optimistic greeting ChatBox pushed.
  // The ref also matches on StrictMode's synthetic second mount after the
  // context value has been cleared on the first mount.
  const shouldBypassForNewConversation = Boolean(
    sessionId === lastCreatedConversationIdRef.current ||
    bypassedNewConversationIdRef.current === sessionId,
  )

  if (shouldBypassForNewConversation) {
    if (bypassedNewConversationIdRef.current !== sessionId) {
      bypassedNewConversationIdRef.current = sessionId
      clearLastCreatedConversationIdRef.current()
    }
    if (didSessionChange) setMessages([])
    setThinking(null)
    streamActivityRef.current = []
    setStreamActivity([])
    setIsStreaming(false)
    setIsLoading(false)
    setError(null)
    setStreamingMessage(null)
    return
  }

  if (didSessionChange) {
    setMessages([])
  }

  // Load messages from server
  setIsLoading(true)
  getConversationChatMessages(token, sessionId, { limit: 100 })
    .then(({ messages: serverMessages }) => {
      if (currentSessionIdRef.current !== requestedSessionId) return
      const clientMessages = serverMessages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(serverToClientMessage)
      // Deduplicate consecutive messages with same role and similar content (save retries / agent echo)
      const normalize = (s: string) => (s || '').replace(/[^a-z0-9]/gi, '').toLowerCase()
      const deduped: typeof clientMessages = []
      for (const msg of clientMessages) {
        const prev = deduped[deduped.length - 1]
        // Onboarding marker messages (the beat transcript + summary) are
        // structurally similar (same marker prefix, shared bundle JSON) and
        // MUST never be collapsed — that would wipe the onboarding chat. Always
        // keep them verbatim.
        const isMarker = (s: string) => typeof s === 'string' && s.startsWith('@@XAVIOR_ONBOARDING')
        if (isMarker(msg.content) || (prev && isMarker(prev.content))) {
          deduped.push(msg)
          continue
        }
        if (prev && prev.role === msg.role) {
          const a = normalize(prev.content)
          const b = normalize(msg.content)
          // Skip if normalized content is identical or one contains the other (minor punctuation diff)
          if (a === b || (a.length > 10 && b.length > 10 && (a.includes(b) || b.includes(a)))) continue
        }
        deduped.push(msg)
      }
      // Use functional update to avoid wiping locally-added messages (e.g. a
      // greeting injected by ChatBox while the fetch was in-flight). If the server
      // returned nothing but local messages already exist, keep them. (#1583)
      setMessages(prev => {
        if (deduped.length === 0 && prev.length > 0) return prev
        return deduped
      })
    })
    .catch((err) => {
      if (currentSessionIdRef.current !== requestedSessionId) return
      console.error('[useChat] Failed to load messages from server:', err)
      // Fall back to empty - don't use localStorage anymore
      setMessages([])
    })
    .finally(() => {
      if (currentSessionIdRef.current !== requestedSessionId) return
      setIsLoading(false)
    })

  setThinking(null)
  streamActivityRef.current = []
  setStreamActivity([])
  setIsStreaming(false)
  setError(null)
  setStreamingMessage(null)
  // Clear any quick-action chips left over from the previous conversation
  // so they don't visually persist into the new one (#1261). The greeting
  // effect re-evaluates against the new conversation since it depends on
  // messages length being 0.
  if (didSessionChange) {
    setPendingQuickActions([])
  }
}
